import React, { useState, useEffect, ReactNode, ErrorInfo } from 'react';
import Layout from './components/Layout';
import BusManager from './components/BusManager';
import SurveyForm from './components/SurveyForm';
import Dashboard from './components/Dashboard';
import CardGenerator from './components/CardGenerator';
import IdCardPrinter from './components/IdCardPrinter';
import AdminManager from './components/AdminManager';
import AttendanceManager from './components/AttendanceManager';
import MySubmissions from './components/MySubmissions';
import QuestionManager from './components/QuestionManager';
import { PaymentsManager } from './components/PaymentsManager';
import { ToastProvider } from './components/Toast';
import StaffRegistrationForm from './components/StaffRegistrationForm';
import StaffManager from './components/StaffManager';
import { User, UserRole } from './types';
import { initStorage, loginUser, logoutUser, registerMasterAdmin, getEmail, createUserProfile, getCurrentUserProfile, updateUser } from './services/storage';
import { auth } from './services/firebase';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Error Boundary Component
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState;
  public props: ErrorBoundaryProps;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.props = props;
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-lg w-full">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">The application encountered an unexpected error.</p>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto mb-4 border border-gray-300">
              {this.state.error?.message}
            </pre>
            <button 
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inline Login Component
const LoginView = ({ 
  onLogin
}: { 
  onLogin: (u: string, p: string) => Promise<boolean>
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Trim inputs to prevent accidental spaces causing issues
      const success = await onLogin(username.trim(), password);
      if (!success) setError('Invalid credentials');
    } catch (err: any) {
      // Show cleaner error messages
      const msg = err.message || 'Action failed';
      if (msg.includes('auth/invalid-credential') || msg.includes('invalid-credential')) {
        setError('Invalid username or password. Please try again.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-800 to-teal-900 flex items-center justify-center p-4 relative">
      <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold text-center text-emerald-800 mb-2">Malayalee Samajam Summer Camp 2026</h1>
        <p className="text-center text-emerald-600 font-bold mb-8">Admin Portal</p>
        
        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
          <div className="flex justify-center mb-2">
            <h2 className="text-xl font-bold text-gray-700">Admin Login</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Username or Email</label>
            <input 
              type="text" 
              className="mt-1 w-full border border-gray-300 rounded-md p-3 focus:ring-emerald-500 focus:border-emerald-500" 
              placeholder="Enter username or email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input 
              type="password" 
              className="mt-1 w-full border border-gray-300 rounded-md p-3 focus:ring-emerald-500 focus:border-emerald-500" 
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded text-sm break-words">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading}
            className={`w-full bg-emerald-600 text-white py-3 rounded-md hover:bg-emerald-700 transition font-bold shadow-md ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Processing...' : 'Login'}
          </button>
        </form>
      </div>

      {/* Developer Badge */}
      <div className="fixed bottom-4 right-4 z-50">
        <span className="bg-emerald-200 text-emerald-900 px-4 py-2 rounded-lg font-bold shadow-lg border-2 border-emerald-400 text-sm">
          Developed by Shabeeb Kayakkandy
        </span>
      </div>
    </div>
  );
};

const GUEST_USER: User = {
  id: 'guest',
  username: 'guest',
  name: 'Guest',
  role: UserRole.REPRESENTATIVE,
  visibleSections: [],
  isActive: true
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [pathname, setPathname] = useState(window.location.pathname);
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin');

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPopState);

    // Listen to Firebase auth state changes to persist session on refresh
    setTimeout(() => { setLoadingAuth(false); }, 3000);
    const unsubscribe = auth.onAuthStateChanged(async (currentUser: any) => {
      console.log("Auth state changed", currentUser);
      if (currentUser) {
        try {
          const profile = await getCurrentUserProfile(currentUser.uid);
          if (profile && profile.isActive !== false) {
            setUser(profile);
            // Auto-select correct view based on role if default is set
            setCurrentView(prev => {
              if (prev && prev !== 'dashboard' && prev !== 'survey') {
                return prev;
              }
              return profile.role === UserRole.REPRESENTATIVE ? 'survey' : profile.role === UserRole.BUS_DRIVER ? 'bus-management' : 'dashboard';
            });
            // Lazy storage initialization
            initStorage().catch(err => {
              const errStr = String(err?.message || err).toLowerCase();
              if (errStr.includes('offline') || err?.code === 'unavailable') {
                console.log("Storage initialization deferred (offline)");
              } else {
                console.warn("Init storage check failed:", err);
              }
            });
          } else {
            setUser(null);
          }
        } catch (err) {
          console.error("Failed to load user session:", err);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoadingAuth(false);
    });

    return () => {
      window.removeEventListener('popstate', onPopState);
      unsubscribe();
    };
  }, []);

  // Removed initStorage from useEffect because it writes to DB and requires auth.
  // It is now called in handleLogin.

  const handleLogin = async (u: string, p: string) => {
    try {
      const foundUser = await loginUser(u, p);
      if (foundUser) {
        // Correct root admin name if it's shabeebkk10@gmail.com
        if (foundUser.username === 'shabeebkk10@gmail.com' && foundUser.name !== 'Shabeeb') {
          foundUser.name = 'Shabeeb';
          updateUser(foundUser.id, { name: 'Shabeeb' }).catch(console.error);
        }
        
        setUser(foundUser);
        setCurrentView(foundUser.role === UserRole.REPRESENTATIVE ? 'survey' : foundUser.role === UserRole.BUS_DRIVER ? 'bus-management' : 'dashboard');
        
        // Initialize storage after successful login (ensures permissions)
        initStorage().catch(err => {
          const errStr = String(err?.message || err).toLowerCase();
          if (errStr.includes('offline') || err?.code === 'unavailable') {
            console.log("Storage initialization deferred (offline)");
          } else {
            console.warn("Init storage background check failed:", err);
          }
        });
        
        return true;
      }
      return false;
    } catch (e: any) {
      // Auto-bootstrap
      const targetEmail = 'shabeebkk10@gmail.com';
      const computedEmail = getEmail(u);

      if (computedEmail === targetEmail) {
         // Check if we are actually logged in to Auth but just missing the Firestore doc
         const currentUser = auth.currentUser;
         if (currentUser && currentUser.email === targetEmail) {
           try {
             console.log("Found Master Admin Auth session but missing profile. Creating profile...");
             const newUser = await createUserProfile(currentUser.uid, u, 'Shabeeb', UserRole.MASTER_ADMIN);
             setUser(newUser);
             setCurrentView('dashboard');
             initStorage().catch(console.error);
             return true;
           } catch (profileErr) {
             console.error("Failed to recover Master Admin profile:", profileErr);
             throw new Error("Profile creation failed. Contact developer.");
           }
         }

         // If we get here, either we weren't signed in or it's a first time registration
         try {
           // Only try registration if loginUser failed (which it did to get here)
           // But if it was an "Invalid credentials" error from loginUser, we should distinguish.
           // However, for the Master Admin, we want to be helpful.
           
           console.log("Attempting to bootstrap Master Admin...");
           const newUser = await registerMasterAdmin('Shabeeb', targetEmail, p);
           setUser(newUser);
           setCurrentView('dashboard');
           
           // Init storage for master admin
           initStorage().catch(console.error);
           
           return true;
         } catch (regError: any) {
           // If registration fails because it already exists, then it's definitely a WRONG PASSWORD
           // because loginUser already failed with this email.
           if (regError.message && (regError.message.includes('already exists') || regError.message.includes('email-already-in-use'))) {
              throw new Error("Invalid username or password. Please check your credentials.");
           }
           console.error("Bootstrap failed:", regError);
           throw regError;
         }
      }
      throw e;
    }
  };

  const handleLogout = async () => {
    await logoutUser();
    setUser(null);
  };

  const renderContent = () => {
    if (!user) return null;

    switch (currentView) {
      case 'dashboard':
        return (user.role === UserRole.MASTER_ADMIN || user.role === UserRole.ADMIN || user.role === UserRole.VIEW_ONLY_ADMIN) ? <Dashboard user={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'payments':
        return user.role !== UserRole.REPRESENTATIVE ? <PaymentsManager currentUser={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'staff':
        return (user.role === UserRole.MASTER_ADMIN || user.role === UserRole.ADMIN || user.role === UserRole.VIEW_ONLY_ADMIN) ? <StaffManager currentUser={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'survey':
        return <SurveyForm user={user} onSuccess={() => setCurrentView(user.role === UserRole.REPRESENTATIVE ? 'my-submissions' : 'dashboard')} />;
      case 'my-submissions':
        return user.role === UserRole.REPRESENTATIVE ? <MySubmissions user={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'card-gen':
        // ALLOW MASTER ADMIN AND ADMIN
        return (user.role === UserRole.MASTER_ADMIN || user.role === UserRole.ADMIN) ? <CardGenerator /> : <div className="p-8 text-center text-red-500 bg-red-50 border border-red-200 rounded-lg">Access Denied: Only Master Admin can access Template Editor.</div>;
      case 'id-card-print':
        return (user.role === UserRole.MASTER_ADMIN || user.role === UserRole.ADMIN || user.role === UserRole.VIEW_ONLY_ADMIN) ? <IdCardPrinter currentUser={user} /> : <div className="p-8 text-center text-red-500 bg-red-50 border border-red-200 rounded-lg">Access Denied</div>;
      
      case 'attendance':
        return (user.role === UserRole.MASTER_ADMIN || user.role === UserRole.ADMIN || user.role === UserRole.VIEW_ONLY_ADMIN) ? <AttendanceManager currentUser={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'bus-management':
        return (user.role === UserRole.MASTER_ADMIN || user.role === UserRole.BUS_DRIVER || user.role === UserRole.VIEW_ONLY_ADMIN) ? <BusManager currentUser={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'admin-users':
        return user.role === UserRole.MASTER_ADMIN ? <AdminManager currentUser={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'settings':
        // RESTRICTED TO MASTER ADMIN
        return user.role === UserRole.MASTER_ADMIN ? <QuestionManager /> : <div className="p-8 text-center text-red-500 bg-red-50 border border-red-200 rounded-lg">Access Denied: Only Master Admin can manage questions.</div>;
      default:
        return <div className="text-center text-gray-500 mt-10">Select an option from the menu</div>;
    }
  };

  if (loadingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="text-gray-500 font-medium animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!user && isAdminRoute) {
    return (
      <ErrorBoundary>
        <LoginView onLogin={handleLogin} />
      </ErrorBoundary>
    );
  }

  if (!isAdminRoute && !user) {
    const isFacultyPath = pathname === '/faculty';
    const isCoordinatorPath = pathname === '/coordinators' || pathname === '/coordinator';

    return (
      <ErrorBoundary>
        <ToastProvider>
          {isFacultyPath ? (
            <StaffRegistrationForm role="faculty" />
          ) : isCoordinatorPath ? (
            <StaffRegistrationForm role="coordinator" />
          ) : (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8 px-4">
               <SurveyForm user={GUEST_USER} onSuccess={() => {
                  window.scrollTo(0, 0);
                  window.location.reload();
               }} />
            </div>
          )}
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Layout user={user!} onLogout={handleLogout} onNavigate={setCurrentView} currentView={currentView}>
          {renderContent()}
        </Layout>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;